import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import UserOne from '../../images/user/user-01.png';
import { useAuth } from '../../auth/hooks/useAuth';

import LazyImage from '../Spinners/LazyImage';
import SvgIcon from '../SvgIcon/SvgIcon';
import useUiVisibility from '../../hooks/ui/useUiVisibility';

import { RootState } from '../../store/store';
import { primaryRoleLabel } from '../../constants/roleLabels';

/**
 * 👤 DropdownUser
 *
 * IMPORTANTE:
 * Este componente ya NO hace fetch de perfil
 * ni carga imagen.
 *
 * Solo consume Redux global.
 *
 * Toda la carga vive en:
 * AuthInitializer
 */
const DropdownUser = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { onLogout, auth } = useAuth();

  const trigger = useRef<any>(null);
  const dropdown = useRef<any>(null);

  // =========================================================
  // ⚙️ Configuración UI global
  // =========================================================

  const { settings, fetchUiSettings } = useUiVisibility();

  const isVisible = (code: string) =>
    settings.find((s) => s.code === code)?.visible ?? true;

  // =========================================================
  // 👤 Redux global
  // =========================================================

  const profilePicture = useSelector(
    (state: RootState) => state.userProfile.userProfile.profilePicture
  );

  // =========================================================
  // ⚙️ Solo cargar settings UI
  // =========================================================

  useEffect(() => {
    fetchUiSettings();
  }, []);

  // =========================================================
  // 🔒 Cerrar click fuera
  // =========================================================

  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;

      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      ) {
        return;
      }

      setDropdownOpen(false);
    };

    document.addEventListener('click', clickHandler);

    return () => document.removeEventListener('click', clickHandler);
  });

  // =========================================================
  // ⌨️ Escape
  // =========================================================

  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;

      setDropdownOpen(false);
    };

    document.addEventListener('keydown', keyHandler);

    return () => document.removeEventListener('keydown', keyHandler);
  });

  // =========================================================
  // 🏷️ Helpers
  // =========================================================

  // Nombre legible del rol principal (incluye los roles de restaurante).
  // Fuente única: src/constants/roleLabels.ts
  const getRoleName = (roles: string[]): string =>
    primaryRoleLabel(roles, 'Sin rol asignado');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailRegex.test(email);
  };

  // =========================================================
  // 🎨 Render
  // =========================================================

  return (
    <div className="relative">
      <Link
        ref={trigger}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="flex items-center gap-4 rounded-lg px-2 transition-all duration-200 hover:bg-gray dark:hover:bg-meta-4"
        to="#"
      >
        {/* 👤 Info */}
        <span className="hidden text-right lg:block">
          <span className="block text-sm font-medium text-black dark:text-white">
            {auth.email && validateEmail(auth.email)
              ? auth.email
              : 'Correo no disponible'}
          </span>

          <span className="block text-xs font-medium text-body dark:text-bodydark">
            {getRoleName(auth.roles)}
          </span>
        </span>

        {/* 🖼️ Avatar */}
        <span className="h-12 w-12 overflow-hidden rounded-full border border-stroke shadow-sm dark:border-strokedark">
          <LazyImage
            src={profilePicture || UserOne}
            alt="User Profile"
            className="h-full w-full object-cover"
          />
        </span>

        {/* ⬇️ Arrow */}
        <span
          className={`transition-transform duration-200 ${
            dropdownOpen ? 'rotate-180' : ''
          }`}
        >
          <SvgIcon name="chevron-down" />
        </span>
      </Link>

      {/* ▼ Dropdown */}
      <div
        ref={dropdown}
        onFocus={() => setDropdownOpen(true)}
        onBlur={() => setDropdownOpen(false)}
        className={`
          absolute right-0 mt-3
          flex w-72 flex-col
          rounded-lg
          border border-stroke
          bg-white
          shadow-default
          dark:border-strokedark
          dark:bg-boxdark
          ${dropdownOpen ? 'block' : 'hidden'}
        `}
      >
        {/* Header */}
        <div className="border-b border-stroke px-5 py-4 dark:border-strokedark">
          <p className="text-sm font-semibold text-black dark:text-white">
            Mi cuenta
          </p>

          <p className="mt-1 text-xs text-body dark:text-bodydark">
            Configuración y acceso rápido
          </p>
        </div>

        {/* Opciones */}
        <ul className="flex flex-col px-2 py-2">
          {isVisible('PROFILE') && (
            <li>
              <Link
                to="/profile"
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-black transition-all duration-200 hover:bg-gray hover:text-primary dark:text-white dark:hover:bg-meta-4"
              >
                <SvgIcon name="user" />
                Mi Perfil
              </Link>
            </li>
          )}

          {isVisible('CONTACTS') && (
            <li>
              <Link
                to="/messages"
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-black transition-all duration-200 hover:bg-gray hover:text-primary dark:text-white dark:hover:bg-meta-4"
              >
                <SvgIcon name="contacts" />
                Mis Contactos
              </Link>
            </li>
          )}

          {isVisible('ACCOUNT') && (
            <li>
              <Link
                to="/pages/settings"
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-black transition-all duration-200 hover:bg-gray hover:text-primary dark:text-white dark:hover:bg-meta-4"
              >
                <SvgIcon name="settings" />
                Mi Cuenta
              </Link>
            </li>
          )}
        </ul>

        {/* Logout */}
        <div className="border-t border-stroke px-2 py-2 dark:border-strokedark">
          <button
            onClick={onLogout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-danger transition-all duration-200 hover:bg-danger/10"
          >
            <SvgIcon name="logout" />
            Cerrar Sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default DropdownUser;
